import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { keyBy } from 'lodash-es';
import { useLocation, useNavigate } from 'react-router-dom';

import withAddIncentiveToCart from 'components/with-add-incentive-to-cart';
import { RedemptionMethod } from 'components/with-add-incentive-to-cart/types';
import { LoyaltyLoader } from 'pages/loyalty/loyalty-loader';
import MenuItemUnavailable from 'pages/menu-content/item-unavailable';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyOffer } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';
import { useUIContext } from 'state/ui';
import { maybeLocaleRawString, maybeLocaleString } from 'utils/graphql';
import { routes } from 'utils/routing';

import ModalSurpriseView from './modal-surprise.view';
import { IModalContent, ModalSurpriseProps } from './types';
import { useFeatureSurpriseModalContent } from './use-feature-surprise-modal-content';

const ModalSurprise = ({ addIncentiveToCart }: ModalSurpriseProps) => {
  const [selectedIncentive, setSelectedIncentive] = useState<LoyaltyOffer>();
  const { loyaltySurpriseOffersEnabled } = useLoyaltyContext();
  const { loading: loyaltyLoading } = useLoyaltyUser();
  const { cartEntries } = useOrderContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsSurpriseOffers);
  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const surpriseAvailable = useAppSelector(selectors.loyalty.selectSurpriseAvailable);
  const dispatch = useAppDispatch();
  const { buildImageUrl } = useUIContext();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: modalContentData, loading: modalContentLoading } = useFeatureSurpriseModalContent();

  const appliedSurprise = appliedOffers.find(offer => Boolean(offer.isSurprise));
  const isCartRoute = location.pathname.startsWith(routes.cart);

  // Loyalty Engine offers map by id
  const userOffersMap = keyBy(userOffers, 'id');

  // Mapping CMS offers to Loyalty Engine offers
  const offers = useMemo(
    () => cmsOffers.filter(offer => userOffersMap[offer?.loyaltyEngineId || '']),
    [cmsOffers, userOffersMap]
  );

  const selectedOfferFromApplied = appliedSurprise
    ? offers.find(offer => offer._id === appliedSurprise?.cmsId)
    : offers[0];

  useEffect(() => {
    if (offers.length) {
      setSelectedIncentive(selectedOfferFromApplied);
    }
  }, [offers.length, selectedOfferFromApplied]);

  const handleDismiss = useCallback(() => {
    dispatch(actions.loyalty.resetSurpriseAvailability());
    if (!isCartRoute) {
      navigate(routes.base);
    }
  }, [isCartRoute, navigate, dispatch]);

  if (loyaltyLoading || modalContentLoading || offersLoading) {
    // Do not render loading animation in cart
    return !isCartRoute ? <LoyaltyLoader /> : null;
  }
  const hideModal =
    !offers?.length || !cartEntries?.length || appliedSurprise || !surpriseAvailable;

  if (!loyaltySurpriseOffersEnabled || (isCartRoute && hideModal)) {
    return null;
  }

  // Do not render offer unavailable in cart
  if (!isCartRoute && hideModal) {
    return <MenuItemUnavailable isOffer />;
  }

  const modalContent: IModalContent = {
    bgImage: buildImageUrl(
      modalContentData?.surpriseAndDelightModalContent?.backgroundImage?.locale || ''
    ),
    noThanksText:
      maybeLocaleString(modalContentData?.surpriseAndDelightModalContent?.noThanksText) || '',
    buttonText:
      maybeLocaleString(modalContentData?.surpriseAndDelightModalContent?.buttonText) || '',
    description:
      maybeLocaleString(modalContentData?.surpriseAndDelightModalContent?.description) || '',
    prompt: maybeLocaleString(modalContentData?.surpriseAndDelightModalContent?.prompt) || '',
    termsText: maybeLocaleRawString(modalContentData?.surpriseAndDelightModalContent?.termsText),
    title: maybeLocaleString(modalContentData?.surpriseAndDelightModalContent?.title) || '',
  };

  const handleAddSurprise = (incentive: LoyaltyOffer) => {
    const surpriseIncentive = {
      ...incentive,
      isStackable: !!userOffersMap[incentive.loyaltyEngineId || '']?.isStackable,
    };
    addIncentiveToCart({
      incentive: surpriseIncentive,
      method: RedemptionMethod.MOBILE,
    });
    dispatch(actions.loyalty.resetSurpriseAvailability());
  };

  return (
    <ModalSurpriseView
      offers={offers}
      selectedOffer={selectedIncentive}
      modalContent={modalContent}
      handleAddSurprise={handleAddSurprise}
      handleSelectOffer={setSelectedIncentive}
      handleDismiss={handleDismiss}
    />
  );
};

export default withAddIncentiveToCart(ModalSurprise);
